import { useTextStream } from '@livekit/components-react';
import { useMemo } from 'react';

import { format } from '../i18n';
import { useStrings } from '../i18n/context';
import { NOTES_TOPIC } from '../protocol';
import { latestNotes } from '../streams';

interface Props {
  readonly open: boolean;
  readonly language: string;
  readonly agents: ReadonlySet<string>;
  readonly onClose: () => void;
}

/**
 * The meeting notes the interpreter writes at the end: summary, decisions, action items.
 * Stays mounted while closed (hidden), because a text stream that arrives with no handler
 * on its topic is dropped. Shows the empty state until the interpreter sends notes on
 * vm.notes. Text is rendered as text, never as HTML.
 */
export function NotesPanel({ open, language, agents, onClose }: Props) {
  const strings = useStrings();
  const { textStreams } = useTextStream(NOTES_TOPIC);
  const notes = useMemo(() => latestNotes(textStreams, agents, language), [textStreams, agents, language]);

  return (
    <aside id="vm-notes" className="notes" aria-labelledby="vm-notes-heading" hidden={!open}>
      <div className="notes__header">
        <h2 id="vm-notes-heading">{strings.notesHeading}</h2>
        <button type="button" className="button" onClick={onClose}>
          {strings.notesClose}
        </button>
      </div>
      {notes ? (
        <div className="notes__body" lang={notes.language || undefined}>
          <h3>{strings.notesSummary}</h3>
          <p>{notes.summary}</p>
          {notes.decisions.length > 0 && (
            <>
              <h3>{strings.notesDecisions}</h3>
              <ul>
                {notes.decisions.map((decision, index) => (
                  // oxlint-disable-next-line react/no-array-index-key -- a fixed list from one notes document
                  <li key={index}>{decision}</li>
                ))}
              </ul>
            </>
          )}
          {notes.actionItems.length > 0 && (
            <>
              <h3>{strings.notesActions}</h3>
              <ul>
                {notes.actionItems.map((item, index) => (
                  // oxlint-disable-next-line react/no-array-index-key -- a fixed list from one notes document
                  <li key={index}>
                    <span>{item.task}</span>
                    {item.owner && <span className="notes__meta">{format(strings.notesOwner, { owner: item.owner })}</span>}
                    {item.due && <span className="notes__meta">{format(strings.notesDue, { due: item.due })}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : (
        <p className="notes__empty">{strings.notesEmpty}</p>
      )}
    </aside>
  );
}
